import { CONSULTATION_STEPS, PostMessageType } from '@abiglobalhealth/go-types'
import { useState } from 'react'

import { closeSession } from '@/api/consultations'
import { getInitialModule } from '@/api/modules'
import { resetTraces } from '@/api/traces'
import { Button } from '@/components/Button'
import { OpenLinkIcon } from '@/components/icons/OpenLinkIcon'
import { Modal } from '@/components/Modal'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useIndications } from '@/hooks/useIndications'
import useLimitExceeded from '@/hooks/useLimitExceeded'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useNavOnly } from '@/hooks/useNavOnly'
import { usePharmacy } from '@/hooks/usePharmacy'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useSurveyCategories } from '@/hooks/useSurveyCategories'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation } from '@/locale/utils'

interface TakeAgainProps {
  closeSessionOnReset?: boolean
  confirmBeforeReset?: boolean
  dataTestId?: string
  onOpenReport?: () => void
}

export const TakeAgain = ({ closeSessionOnReset = false, confirmBeforeReset = false, dataTestId, onOpenReport }: TakeAgainProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { showLoading, hideLoading } = useLoading()
  const { user } = useUser()
  const { updateStep } = useConsultationStep()
  const { updateIndicationCategory } = useSurveyCategories()
  const { consultationType } = useConsultationType()
  const { updateModule } = useModule()
  const { clearIndications } = useIndications()
  const { clearSelectedPharmacy } = usePharmacy()
  const { checkLimitExceeded } = useLimitExceeded()
  const { sendPostMessage } = usePostMessage()
  const { isNavOnly, returnToNavigator } = useNavOnly()

  const handleReset = async () => {
    try {
      await sendPostMessage(PostMessageType.END_CONSULTATION_CLICK, { consultationType })
      showLoading()

      const payload = { uniqueId: user?.data?.uniqueId as string }
      await (closeSessionOnReset ? closeSession(payload) : resetTraces(payload))

      if (isNavOnly) return returnToNavigator()

      const initialModule = await getInitialModule({
        uniqueId: user?.data?.uniqueId,
        userId: user?.data?.userId,
        serviceType: consultationType
      })
      updateModule(initialModule.module)
      await checkLimitExceeded(consultationType)
      updateIndicationCategory(null)
      clearIndications()
      clearSelectedPharmacy()

      updateStep(CONSULTATION_STEPS.LANDING)
    } finally {
      hideLoading()
    }
  }

  return (
    <>
      <Button
        custom
        customClassName="bg-white text-primary text-sm"
        data-testid={dataTestId ?? (confirmBeforeReset ? 'report-close' : 'take-again-button')}
        onClick={confirmBeforeReset ? () => setShowConfirmation(true) : handleReset}
      >
        {getSlugTranslation('general.consultations.answer.end_consultation')}
      </Button>

      <Modal
        className="z-30"
        dataTestId="report-exit-dialog"
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title={getSlugTranslation('general.consultation.closingModal.title')}
      >
        <p className="text-center">{getSlugTranslation('general.consultation.closingModal.description')}</p>

        <div className="flex flex-col gap-4 pt-6">
          <Button customClassName="flex items-center justify-center gap-2 text-sm" hasShadow={false} onClick={onOpenReport}>
            <OpenLinkIcon className="h-5 w-5" />
            {getSlugTranslation('general.consultation.openReportButton')}
          </Button>
          <Button
            custom
            customClassName="border-primary text-primary border text-sm"
            data-testid="report-exit-done"
            hasShadow={false}
            onClick={handleReset}
          >
            {getSlugTranslation('button.done')}
          </Button>
        </div>
      </Modal>
    </>
  )
}
